/**
 * Supported worker types for async job processing
 */
export enum WorkerType {
  LIST_PARSER = 'listParser',
  PRINTER = 'printer',
  PICTURE_PROCESSOR = 'pictureProcessor',
}

/**
 * Label parameter types from LabelPrintingPage
 */
export interface LabelPrintingParameters {
  partNumber?: string;
  part?: string; // For external parts
  id?: string; // For shipping labels
  description?: string;
  serialNumber?: string;
  trackingNumber?: string;
  recipientName?: string;
  address?: string;
  binId?: string;
  locationCode?: string;
  capacity?: string;
  orderNumber?: string;
  customerName?: string;
  shipToAddress?: string;
  weight?: number;
}

/**
 * Printer preview request type
 */
export interface PrinterPreviewRequest {
  labelType: string;
  parameters: LabelPrintingParameters;
  quantity: number;
}

/**
 * Printer preview response type
 */
export interface PrinterPreviewResponse {
  success: boolean;
  zpl: string;
  labelType: string;
  timestamp: string;
  error?: string;
}

export interface SubmitPictureResponse {
  jobId: string;
  partNumber: string;
  status: string;
}

// API Endpoints
export const ENDPOINTS = {
  JOBS_SUBMIT: (workerType: string) => `jobs/${workerType}/submit`,
  JOBS_STATUS: (jobId: string) => `jobs/${jobId}/status`,
  JOBS_RESULT: (jobId: string) => `jobs/${jobId}/result`,
  LIST_PARSER_PRICE_LISTS: () => `list-parser/price-lists`,
  LIST_PARSER_DATA: () => `list-parser/data`,
  PRINTER_PREVIEW: () => `printer/preview`,
  PRINTERS_LIST: () => `printers`,
  PRINTERS_CREATE: () => `printers`,
  PRINTERS_UPDATE: (printerId: string) =>
    `printers/${encodeURIComponent(printerId)}`,
  PRINTERS_DELETE: (printerId: string) =>
    `printers/${encodeURIComponent(printerId)}`,
  PRINTERS_FORCE_DELETE: (printerId: string) =>
    `printers/${encodeURIComponent(printerId)}?force=true`,
  PRINTERS_STATUS: (printerId: string) =>
    `printers/${encodeURIComponent(printerId)}/status`,
  USER_PREFERENCES: () => `user-preferences`,
  PARTS_GET: () => `parts`,
  PARTS_LOOKUP_UM: (partNumUM: string) =>
    `parts/lookup-um/${encodeURIComponent(partNumUM)}`,
  BINS_GET: () => `parts/bins`,
  PARTS_BINS: (partNum: string) => `parts/${encodeURIComponent(partNum)}/bins`,
  PARTS_TIME_PHASED: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/time-phased`,
  PARTS_PURCHASE_HIST: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/purchase-hist`,
  PARTS_APPROVED_SUPPLIERS: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/approved-suppliers`,
  PARTS_TRANS_HIST: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/trans-hist`,
  PARTS_QUOTE_HISTORY: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/quote-history`,
  PARTS_CUSTOMER_REFS: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/customer-refs`,
  PARTS_ORDER_HISTORY: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/order-history`,
  PARTS_PROD_HISTORY: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/prod-history`,
  PARTS_CUSTOMER_PRICE: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/customer-price`,
  PARTS_METHOD: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/method`,
  PARTS_WHERE_USED: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/where-used`,
  PARTS_DRAWING_CHECK: (drawNum: string) =>
    `parts/drawing/${encodeURIComponent(drawNum)}/check`,
  PARTS_DRAWING: (drawNum: string) =>
    `parts/drawing/${encodeURIComponent(drawNum)}`,
  FULFILLMENT_GET: () => `fulfillment/items`,
  SALES_ORDER_GET: (orderNum: number) => `sales-orders/${orderNum}`,
  SALES_ORDER_PICK_LIST: (orderNum: number) =>
    `sales-orders/${orderNum}/pick-list`,
  SALES_ORDER_SHIPMENTS: (orderNum: number) =>
    `sales-orders/${orderNum}/shipments`,
  PURCHASE_ORDER_GET: (poNum: number) => `purchase-orders/${poNum}`,
  PURCHASE_ORDER_RECEIPTS: (poNum: number) =>
    `purchase-orders/${poNum}/receipts`,
  SCHEDULING_PRODUCTION: (
    startDate: string,
    endDate: string,
    resourceGrpID?: string
  ) =>
    `scheduling/production?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}${resourceGrpID ? `&resourceGrpID=${encodeURIComponent(resourceGrpID)}` : ''}`,
  SCHEDULING_CALENDAR: (calendarID: string) =>
    `scheduling/calendar?calendarID=${encodeURIComponent(calendarID)}`,
  JOB_TRACKER_GET: (jobNum: string) => `job-tracker/${jobNum}`,
  JOB_TRACKER_PICK_LIST: (jobNum: string) =>
    `job-tracker/${encodeURIComponent(jobNum)}/pick-list`,
  JOB_TRACKER_PO_RELS: (jobNum: string) =>
    `job-tracker/${encodeURIComponent(jobNum)}/po-rels`,
  PICTURE_PROCESSOR_SUBMIT: () => `picture-processor/submit`,
  PICTURE_MANAGER_RECENT: (page: number, limit: number, partNumber?: string) =>
    `picture-manager/recent?page=${page}&limit=${limit}${partNumber ? `&partNumber=${encodeURIComponent(partNumber)}` : ''}`,
  PICTURE_MANAGER_RECENT_PARTS: (
    page: number,
    limit: number,
    partNumber?: string
  ) =>
    `picture-manager/recent-parts?page=${page}&limit=${limit}${partNumber ? `&partNumber=${encodeURIComponent(partNumber)}` : ''}`,
  PICTURE_MANAGER_PART: (partNumber: string) =>
    `picture-manager/parts/${encodeURIComponent(partNumber)}`,
  PICTURE_MANAGER_REORDER: (partNumber: string) =>
    `picture-manager/parts/${encodeURIComponent(partNumber)}/reorder`,
  PICTURE_MANAGER_DELETE: (partNumber: string, index: number) =>
    `picture-manager/parts/${encodeURIComponent(partNumber)}/photos/${index}`,
  QUOTES_GET: () => `quotes`,
  QUOTE_DETAIL: (quoteNum: number) => `quotes/${quoteNum}`,
  // Customer dashboard endpoints
  CUSTOMERS_SEARCH: (q: string) =>
    `customers/search?q=${encodeURIComponent(q)}`,
  CUSTOMER_DETAIL: (custNum: number) => `customers/${custNum}`,
  CUSTOMER_QUOTES: (custNum: number, shipToNum?: string) =>
    `customers/${custNum}/quotes${shipToNum ? `?shipTo=${encodeURIComponent(shipToNum)}` : ''}`,
  CUSTOMER_ORDERS: (custNum: number, shipToNum?: string) =>
    `customers/${custNum}/orders${shipToNum ? `?shipTo=${encodeURIComponent(shipToNum)}` : ''}`,
  // Quote-to-Cash dashboard sections
  QUOTE_TO_CASH: (section: string, view?: string) =>
    `quote-to-cash/${section}${view ? `?view=${view}` : ''}`,
  // Sales inbox agent page
  SALES_AGENT_EMAILS: (month: string) =>
    `sales-agent/emails?month=${encodeURIComponent(month)}`,
  SALES_AGENT_REVIEW: (emailKey: string) =>
    `sales-agent/emails/${encodeURIComponent(emailKey)}/review`,
  SALES_AGENT_APPROVAL: (emailKey: string) =>
    `sales-agent/emails/${encodeURIComponent(emailKey)}/approval`,
  // Part Demand & Forecast dashboard
  PART_DEMAND: (partNum: string) =>
    `parts/${encodeURIComponent(partNum)}/demand`,
  // Docs endpoints
  DOCS_HOME: () => `docs/home`,
  DOCS_SEARCH: () => `docs/search`,
  DOCS_PROCESS: (idOrSlug: string) =>
    `docs/processes/${encodeURIComponent(idOrSlug)}`,
  DOCS_PROCEDURE: (idOrSlug: string) =>
    `docs/procedures/${encodeURIComponent(idOrSlug)}`,
} as const;

export interface ApiResponse<T> {
  statusCode: number;
  headers: Record<string, string>;
  body: {
    success: boolean;
    data?: T;
    error?: Error;
  };
}

export interface Error {
  message: string;
  code: string;
}

// Types for ListParser API
export interface PriceList {
  id: string;
  description: string;
  currency?: string;
}

export interface GetPriceListsResponse {
  success: boolean;
  priceLists: PriceList[];
  error?: string;
}

// One input row of a list parser run (GET /list-parser/data)
export interface ListParserDataMatch {
  matchType: string;
  umPart: string;
  umDescription: string;
}

// Rows saved before matches were recorded have no outputFile, matched or matches
export interface ListParserDataItem {
  jobId: string;
  rowNumber: string;
  timestamp: string;
  userName?: string;
  file: string;
  fileUrl: string;
  fileName?: string;
  worksheet: string;
  machineType: string;
  priceList: string;
  discountPct: number;
  partNumCol: number;
  descCol: number;
  qtyCol: number;
  fileReference: string;
  fileQty: string;
  fileDesc: string;
  fileRawRow: string;
  outputFile?: string;
  outputFileUrl?: string;
  matched?: boolean;
  matches?: ListParserDataMatch[];
  // Runs from before the table were matched again on this date: not what the customer saw
  rematchedAt?: string;
}

export interface ListParserDataQuery {
  from?: string;
  to?: string;
  partNumber?: string;
  user?: string;
  fileName?: string;
  matchType?: string;
  jobId?: string;
  machineType?: string;
  priceList?: string;
  matched?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface GetListParserDataResponse {
  success: boolean;
  items?: ListParserDataItem[];
  nextCursor?: string;
  message?: string;
}

export interface ListParserRequest {
  file: File;
  worksheetName: string;
  priceList: string;
  machineType: 'SIDEL' | 'KHS' | 'KRONES' | 'N/A';
  columnMappings?: {
    partNumber: number;
    description: number;
    quantity: number;
  };
  enableColorCoding?: boolean;
  discountPercentage?: number;
}

export interface ListParserResponse {
  message: string;
  input_s3_key: string;
  output_s3_key: string;
  total_parts: number;
  matched_parts: number;
  unmatched_parts: number;
  match_summary: Array<{
    customer_part: string;
    match_type: string;
    um_part: string;
    um_price: number;
    notes: string;
  }>;
  presigned_url: string;
}

/**
 * Printer job request type
 */
export interface PrinterJobRequest {
  labelType: string;
  parameters: LabelPrintingParameters;
  quantity: number;
  printerId?: string;
}

/**
 * Mirror this with ADMIN_CLEARANCE_LEVEL in
 * ToolsAPI/src/application/frameworks/express/middleware/requireAdmin.ts
 */
export const ADMIN_CLEARANCE_LEVEL = 3;

export type PrinterKind = 'label';

export interface Printer {
  printerId: string;
  name: string;
  ip: string;
  port: number;
  kind: PrinterKind;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface PrinterCreateRequest {
  name: string;
  ip: string;
  port?: number;
  kind?: PrinterKind;
  enabled?: boolean;
}

export interface PrinterUpdateRequest {
  name?: string;
  ip?: string;
  port?: number;
  enabled?: boolean;
}

export interface ListPrintersResponse {
  success: boolean;
  printers?: Printer[];
  error?: string;
}

export interface PrinterMutationResponse {
  success: boolean;
  printer?: Printer;
  error?: string;
}

export interface PrinterStatusResponse {
  success: boolean;
  status?: 'online' | 'offline';
  ip?: string;
  port?: number;
  timestamp?: string;
  printerId?: string;
  name?: string;
  error?: string;
}

export interface UserPreferences {
  userId: string;
  lastPrinterId?: string;
  updatedAt: string;
}

export interface UserPreferencesResponse {
  success: boolean;
  preferences?: UserPreferences;
  error?: string;
}

/**
 * Generic job request type - can be extended for different worker types
 */
export type JobRequest = ListParserRequest | PrinterJobRequest;

/**
 * Generic job response type - can be extended for different worker types
 */
export type JobResponse = ListParserResponse;

export interface ApiError {
  success: false;
  error: string;
}

// Types for async job processing
export type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export interface JobSubmitResponse {
  jobId: string;
  status: JobStatus;
  userName: string;
  createdAt: string;
}

export interface JobStatusResponse {
  jobId: string;
  status: JobStatus;
  userName: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
  jobProgress?: number;
}

export interface JobSummary {
  total_parts: number;
  matched_parts: number;
  unmatched_parts: number;
}

export interface JobResultResponse {
  presignedUrl: string;
  outputS3Key: string;
  expiresAt: string;
  jobSummary?: JobSummary;
}

// Types for Parts API
export interface Part {
  part: string;
  erpPartNum: string;
  descriptionEN: string;
  descriptionFR: string;
  fullSearch?: string;
}

export interface PartBin {
  whseBinDescription: string;
  partBinOnhandQty: number;
}

export interface Bin {
  binNum: string;
  description: string;
}

export interface PartTimePhased {
  partDtlDueDate: string;
  calculatedTransQty: number;
  calculatedInfo: string;
}

export interface PurchaseHist {
  poNum: number;
  vendorNum: number;
  orderDate: string;
  partNum: string;
  pum: string;
  baseQty: number;
  vendorName: string;
  leadTime: number;
  unitCost: number;
  status: string;
}

export interface ApprovedSupplier {
  vendorNum: number;
  vendorName: string;
  venPartNum: string;
}

export interface TransHistory {
  tranDate: string;
  tranQty: number;
  runningTotal: number;
  humanReadable: string;
}

export interface QuoteHistory {
  quoteNum: number;
  shipToName: string;
  dateQuoted: string;
  expectedQty: number;
  /** Base unit price before any line discount */
  expectedUnitPrice: number;
  /** Line discount percentage, 0 when the line was not discounted */
  discountPercent: number;
  /** Unit price after the line discount */
  netUnitPrice: number;
  currencyCode: string;
  partNum: string;
  ordered: boolean;
  rowIdent: string;
}

export interface CustomerRef {
  custPartNum: string;
  descriptionFR: string;
  descriptionEN: string;
}

export interface ProdHistory {
  closedDate: string;
  revisionNum: string;
  jobNum: string;
  qtyCompleted: number;
  unitCost: number;
}

export interface CustomerPriceBreak {
  quantity: number;
  prices: {
    [priceListCode: string]: number;
  };
}

export interface CustomerPriceList {
  partNum: string;
  priceListCodes: string[];
  priceBreaks: CustomerPriceBreak[];
}

export interface PartDetails extends Part {
  // Unit of Measure
  ium: string;

  // Type and status
  typeCode: string;
  nonStock: boolean;

  // Notes and locations
  notes: string;
  oemSampleLocation: string;
  pickingLocation: string;

  // Hold status
  onHold: boolean;
  onHoldDate: string | null;
  onHoldReasonCode: string;

  // Web and inventory
  webPart: boolean;
  minimumQty: number;
  minMfgLotSize: number;

  isInactive: boolean;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface GetPartsResponse {
  success: boolean;
  parts: Part[];
  pagination?: PaginationMeta;
  error?: string;
}

export interface GetPartDetailsResponse {
  success: boolean;
  partDetails?: PartDetails;
  error?: string;
  code?: string;
}

export interface GetPartBinsResponse {
  success: boolean;
  partBins?: PartBin[];
  error?: string;
}

export interface GetBinsResponse {
  success: boolean;
  bins?: Bin[];
  error?: string;
}

export interface GetPartTimePhasedResponse {
  success: boolean;
  timePhased?: PartTimePhased[];
  error?: string;
}

export interface GetPartPurchaseHistResponse {
  success: boolean;
  purchaseHist?: PurchaseHist[];
  error?: string;
}

export interface GetPartApprovedSuppliersResponse {
  success: boolean;
  approvedSuppliers?: ApprovedSupplier[];
  error?: string;
}

export interface GetPartTransHistResponse {
  success: boolean;
  transHist?: TransHistory[];
  morePages?: boolean;
  page?: number;
  pageSize?: number;
  error?: string;
}

export interface GetPartQuoteHistResponse {
  success: boolean;
  quoteHistory?: QuoteHistory[];
  error?: string;
}

export interface GetPartCustomerRefsResponse {
  success: boolean;
  customerRefs?: CustomerRef[];
  error?: string;
}

export interface OrderHistory {
  orderNum: number;
  customerName: string;
  orderDate: string;
  orderQty: number;
  /** Base unit price before any line discount */
  unitPrice: number;
  /** Line discount percentage, 0 when the line was not discounted */
  discountPercent: number;
  /** Unit price after the line discount */
  netUnitPrice: number;
  openLine: boolean;
  currencyCode: string;
}

export interface GetPartOrderHistResponse {
  success: boolean;
  orderHistory?: OrderHistory[];
  error?: string;
}

export interface GetPartProdHistResponse {
  success: boolean;
  prodHistory?: ProdHistory[];
  error?: string;
}

export interface GetCustomerPriceResponse {
  success: boolean;
  customerPriceList?: CustomerPriceList;
  error?: string;
}

// Types for Method/BOM Tree API
export interface PartOpr {
  opSeq: number;
  opCode: string;
  opDesc: string;
  // Timing fields
  estSetHours: number;
  prodStandard: number;
  stdFormat: string;
}

export interface PartMtl {
  mtlSeq: number;
  partNumUM: string;
  qtyPer: number;
  partNum: string;
  expandTree: boolean;
  partDescription: string;
  relatedOperation: number;
  // Inventory fields
  onHandQty: number;
  demandQty: number;
  availNowQty: number;
  supplyQty: number;
  availLaterQty: number;
}

export interface PartMtlNode extends PartMtl {
  operations?: PartOpr[];
  materials?: PartMtlNode[];
}

export interface MethodNode {
  partNum: string;
  drawNum: string;
  revisionNum: string;
  partDescription: string;
  approved: boolean;
  approvedDate: string;
  approvedBy: string;
  operations: PartOpr[];
  materials: PartMtlNode[];
}

export interface GetMethodResponse {
  success: boolean;
  methodData?: MethodNode;
  error?: string;
}

/**
 * Where Used information - shows parent assemblies that use a specific part
 */
export interface WhereUsed {
  partNum: string;
  partNumUM: string;
  revisionNum: string;
  partDescription: string;
  qtyPer: number;
  fixedQty: boolean;
  partInactive: boolean;
  revisionApproved: boolean;
}

export interface GetWhereUsedResponse {
  success: boolean;
  whereUsed?: WhereUsed[];
  error?: string;
}

export interface CheckDrawingResponse {
  success: boolean;
  available: boolean;
  drawNum: string;
  error?: string;
}

export interface GetDrawingResponse {
  success: boolean;
  url?: string;
  expiresIn?: number;
  error?: string;
}

// Types for UM Part Number Lookup
export interface LookupUMPartResponse {
  success: boolean;
  found: boolean;
  erpPartNum: string | null;
  error?: string;
}

// Types for Fulfillment API
export interface FulfillmentItem {
  id: string;
  calculatedDemand: string;
  customerLocation: string;
  part: string;
  erpPartNum: string;
  partDescription: string;
  orderedQuantity: number;
  onHandQuantity: number | null;
  shippedQuantity: number | null;
  requestDate: string;
  totalPriceCAD: number;
  customerPartNumber: string | null;
  fulfillmentPercentage: number;
  orderAge: number | null;
  daysLate: number | null;
  orderDate: string | null;
  creditHold: boolean;
  creditHoldReason: string;
}

export interface FulfillmentPaginationMeta {
  offset: number;
  limit: number;
  total: number;
  totalBacklog: number;
}

export interface GetFulfillmentItemsResponse {
  success: boolean;
  items: FulfillmentItem[];
  pagination: FulfillmentPaginationMeta;
  sortBy?: string;
  sortOrder?: string;
  error?: string;
}

export enum LabelType {
  INTERNAL_PART = 'internal-part',
  EXTERNAL_PART = 'external-part',
  SHIPPING = 'shipping',
  BIN = 'bin',
}

export interface InternalPartLabelParameters {
  partNumber?: string;
}

export interface ExternalPartLabelParameters {
  part?: string;
}

export interface ShippingLabelParameters {
  id?: string;
}

export interface BinLabelParameters {
  description?: string;
}

export type LabelParameters =
  | InternalPartLabelParameters
  | ExternalPartLabelParameters
  | ShippingLabelParameters
  | BinLabelParameters;

// Types for Sales Order API
export interface SalesOrderHeader {
  orderNum: number;
  custNum: number;
  custId: string;
  customerName: string;
  poNum: string;
  orderDate: string;
  requestDate: string;
  needByDate: string;
  // Ship To information
  shipToNum: string;
  shipToName: string;
  shipToAddress: string;
  shipToContactName: string;
  // Bill To information
  btCustId: string;
  btCustName: string;
  btAddress: string;
  btContactName: string;
  // Sold To information
  soldToAddress: string;
  // General order info
  shipViaDescription: string;
  currencyCode: string;
  totalCharges: number;
  docTotalCharges: number; // Lines before their discounts, before tax
  docTotalDiscount: number;
  openOrder: boolean;
  voidOrder: boolean;
  orderHeld: boolean;
  salesRepName: string;
  orderComment: string;
  shipComment: string;
  entryPerson: string;
  totalLines: number;
  creditHold: boolean;
  creditHoldReason: string;
  /** The whole order must ship in one shipment */
  shipOrderComplete: boolean;
}

export interface SalesOrderLine {
  orderLine: number;
  partNum: string;
  partNumUM: string;
  custPartNum: string;
  lineDesc: string;
  orderQty: number;
  ium: string;
  /** Base (list) unit price before discount */
  docUnitPrice: number;
  /** Line discount percentage */
  discountPercent: number;
  /** Net unit price after discount */
  docOrderBasedPrice: number;
  docExtPrice: number;
  openLine: boolean;
  /** The line must ship in one shipment */
  shipLineComplete: boolean;
  totalShipped: number;
  onHandQty: number;
}

export interface SalesOrder {
  header: SalesOrderHeader;
  lines: SalesOrderLine[];
}

export interface GetSalesOrderResponse {
  success: boolean;
  salesOrder?: SalesOrder;
  error?: string;
}

export interface SalesOrderShipmentLine {
  orderLine: number;
  /** ERP part number - used for the Part dashboard link, not displayed */
  partNum: string;
  partNumUM: string;
  lineDesc: string;
  inventoryShipQty: number;
  /** Bin the inventory quantity was shipped from */
  warehouseBin: string;
  jobShipQty: number;
  /** Job the job quantity was shipped from */
  jobNum: string;
}

export interface SalesOrderShipment {
  packNum: number;
  shipDate: string;
  shipPerson: string;
  shipViaCode: string;
  trackingNumber: string;
  lines: SalesOrderShipmentLine[];
}

export interface GetSalesOrderShipmentsResponse {
  success: boolean;
  shipments?: SalesOrderShipment[];
  error?: string;
}

// Types for Purchase Order API
export interface PurchaseOrderHeader {
  poNum: number;
  vendorNum: number;
  vendorId: string;
  vendorName: string;
  orderDate: string;
  buyerId: string;
  buyerName: string;
  openOrder: boolean;
  voidOrder: boolean;
  orderHeld: boolean;
  currencyCode: string;
  shipVia: string;
  fob: string;
  terms: string;
  entryPerson: string;
  docTotalOrder: number;
  comment: string;
}

export interface PurchaseOrderRelease {
  poRelNum: number;
  dueDate: string;
  relQty: number;
  receivedQty: number;
  openRelease: boolean;
  voidRelease: boolean;
}

export interface PurchaseOrderLine {
  poLine: number;
  lineType: 'inventory' | 'subcontract' | 'nonStock' | 'other';
  /** The job a subcontract line works for, '' otherwise */
  jobNum: string;
  /** That job's operation, 0 otherwise */
  jobOprSeq: number;
  partNum: string;
  partNumUM: string;
  /** Supplier's part number */
  venPartNum: string;
  lineDesc: string;
  /** Ordered quantity in the purchasing unit of measure (uom) */
  orderQty: number;
  uom: string;
  docUnitCost: number;
  docExtCost: number;
  dueDate: string;
  openLine: boolean;
  voidLine: boolean;
  releases: PurchaseOrderRelease[];
}

export interface PurchaseOrder {
  header: PurchaseOrderHeader;
  lines: PurchaseOrderLine[];
}

export interface GetPurchaseOrderResponse {
  success: boolean;
  purchaseOrder?: PurchaseOrder;
  error?: string;
}

export interface PurchaseOrderReceiptLine {
  packLine: number;
  poLine: number;
  poRelNum: number;
  partNum: string;
  partNumUM: string;
  lineDesc: string;
  /** Supplier quantity (pum), like the PO line's quantities */
  vendorQty: number;
  pum: string;
  /** Same quantity in UM's unit (ium) */
  ourQty: number;
  ium: string;
  receivedTo: 'stock' | 'inspection' | 'subcontract' | 'other';
  binNum: string;
  jobNum: string;
}

/** The lines of one packing slip received on one date */
export interface PurchaseOrderReceipt {
  packSlip: string;
  receiptDate: string;
  receivedBy: string;
  shipVia: string;
  lines: PurchaseOrderReceiptLine[];
}

export interface GetPurchaseOrderReceiptsResponse {
  success: boolean;
  receipts?: PurchaseOrderReceipt[];
  error?: string;
}

// Types for Production Schedule API
export interface SchedulingBoardEntry {
  // Job identification
  JobNum: string;
  AsmNum: number;
  OprNum: number;
  OpDtlNum: number;

  // Operation details
  OpCode: string;
  OpDesc: string;

  // Resource information
  ResourceGrpID: string;
  ResourceGrpIDDesc: string;
  ResourceID: string;
  ResourceIDDesc: string;
  SchedResourceGrpID: string;
  SchedResourceID: string;
  SchedResourceIDDesc: string;

  // Part information
  JobPartNum: string;
  JobPartDesc: string;
  JobPartNumUM: string;
  AsmPartNum: string;
  AsmPartDesc: string;

  // Scheduling dates and times
  OSDate: string;
  OSHour: number;
  ODDate: string;
  ODHour: number;
  StartDate: string;
  ReqDueDate: string;

  // Duration and progress
  TotalEstHours: number;
  TotalActHours: number;
  RemainHours: number;
  PrctCmplt: number;
  EstDurHours: number;

  // Quantities
  ProdQty: number;
  RunQty: number;
  JobHeadQtyCompleted: number;
  JobOperQtyCompleted: number;

  // Status flags
  DaysLate: number;
  Lateness: number;
  OnCritPath: boolean;
  OpComplete: boolean;
  OperationStarted: boolean;
  ProductionComplete: boolean;
  SetupComplete: boolean;
  SchedLocked: boolean;
  JobLocked: boolean;
  OprSubcontract: boolean;
  LastOper: boolean;

  // Job status
  JobType: string;
  JobStatus: string;
  JobLateStatus: string;

  // Machine info
  NumMachines: number;
  Machines: number;

  // Customer
  CalcCustID: string;

  // Work info
  WIMode: string;
  WIStatus: string;
  WIName: string;

  // Comments
  SchedComment: string;

  // Identifiers
  SysRowID: string;
  Company: string;
  Plant: string;

  // Sorting fields
  SortStartDate: string;
  SortJob: string;
  SortPart: string;
  SortCustID: string;
  SortDueDate: string;
  SortDaysLate: string;
  SortOpCode: string;
}

export interface GetProductionScheduleResponse {
  success: boolean;
  schedulingBoard?: SchedulingBoardEntry[];
  error?: string;
}

/**
 * Production Calendar from Erp.BO.ProdCalSvc
 * Contains 168 hours representing each hour of the week.
 * Hour 0 = Sunday midnight, Hour 167 = Saturday 11PM
 * true = open, false = closed
 */
export interface ProductionCalendar {
  CalendarID: string;
  Description: string;
  hours: boolean[];
}

export interface GetProductionCalendarResponse {
  success: boolean;
  calendar?: ProductionCalendar;
  error?: string;
}

// ========================================
// Job Tracker Types
// ========================================

export interface JobHead {
  company: string;
  jobNum: string;
  partNum: string;
  partNumUM: string;
  revisionNum: string;
  drawNum: string;
  partDescription: string;
  prodQty: number;
  iUM: string;
  startDate: string;
  dueDate: string;
  reqDueDate: string;
  jobType: string;
  jobClosed: boolean;
  jobComplete: boolean;
  jobCompletionDate: string | null;
  jobEngineered: boolean;
  jobReleased: boolean;
  jobFirm: boolean;
  jobHeld: boolean;
  closedDate: string | null;
  schedStatus: string;
  qtyCompleted: number;
  plant: string;
  createdBy: string;
  createDate: string;
}

export interface JobOper {
  company: string;
  jobNum: string;
  assemblySeq: number;
  oprSeq: number;
  opCode: string;
  opDesc: string;
  opComplete: boolean;
  prodComplete: boolean;
  setupComplete: boolean;
  qtyCompleted: number;
  productionQty: number;
  startDate: string;
  dueDate: string;
  actualStartDate: string | null;
  actualEndDate: string | null;
  estProdHours: number;
  estSetHours: number;
  actProdHours: number;
  actSetupHours: number;
  subContract: boolean;
  vendorNum: number | null;
  vendorName: string | null;
  poNum: number | null;
  commentText: string;
}

export interface JobMtl {
  company: string;
  jobNum: string;
  assemblySeq: number;
  mtlSeq: number;
  partNum: string;
  partNumUM: string;
  description: string;
  requiredQty: number;
  issuedQty: number;
  iUM: string;
  reqDate: string;
  relatedOperation: number;
  issuedComplete: boolean;
  backFlush: boolean;
  direct: boolean;
  availableQty: number;
  allocatedQty: number;
  reservedQty: number;
}

export interface JobPart {
  company: string;
  jobNum: string;
  partNum: string;
  partNumUM: string;
  revisionNum: string;
  partsPerOp: number;
  partQty: number;
  stockQty: number;
  shippedQty: number;
  receivedQty: number;
  wipQty: number;
  qtyCompleted: number;
  reservedQty: number;
  /** Cost factors: relative weights for splitting the job's labour and burden, and its material, between its parts */
  lbrCostBase: number;
  mtlCostBase: number;
  jobClosed: boolean;
  jobComplete: boolean;
  plant: string;
  partDescription: string;
  iUM: string;
}

export interface JobProd {
  company: string;
  jobNum: string;
  orderNum: number | null;
  orderLine: number;
  orderRelNum: number;
  prodQty: number;
  shippedQty: number;
  receivedQty: number;
  wipQty: number;
  partNum: string;
  plant: string;
  custID: string;
  custName: string;
}

export interface JobDataset {
  jobHead: JobHead | null;
  jobOper: JobOper[];
  jobMtl: JobMtl[];
  jobPart: JobPart[];
  jobProd: JobProd[];
}

export interface GetJobDataResponse {
  success: boolean;
  jobData?: JobDataset;
  error?: string;
}

export interface PORelInfo {
  assemblySeq: number;
  oprSeq: number;
  vendorName: string;
  poNum: number;
  dueDate: string;
}

export interface GetJobPORelsResponse {
  success: boolean;
  poRels?: PORelInfo[];
  error?: string;
}

// ========================================
// Picture Manager Types
// ========================================

export interface PictureItem {
  partNumber: string;
  index: number;
  thumbnailUrl: string;
  fullUrl: string;
  lastModified: string;
}

export interface GetRecentPicturesResponse {
  success: boolean;
  pictures?: PictureItem[];
  total?: number;
  page?: number;
  totalPages?: number;
  error?: string;
}

export interface GetPartPicturesResponse {
  success: boolean;
  partNumber?: string;
  pictures?: PictureItem[];
  error?: string;
}

export interface RecentPartGroup {
  partNumber: string;
  mostRecentUpload: string;
  pictures: PictureItem[];
}

export interface GetRecentPartsResponse {
  success: boolean;
  parts?: RecentPartGroup[];
  total?: number;
  page?: number;
  totalPages?: number;
  error?: string;
}

// ===== Quote Dashboard Types =====

export type AgentStatus = 'drafted' | 'skipped_by_claude' | 'no_thread_found';

export interface OpenQuote {
  quoteNum: number;
  custNum: number;
  customerName: string;
  custId: string;
  shipToNum: string;
  shipToName: string;
  dateQuoted: string;
  followUpDate: string;
  docTotalQuote: number;
  agentCycleNum: number | null;
  agentStatus: AgentStatus | null;
  agentRunDate: string | null;
  agentReason: string | null;
  agentQuoteDescription: string | null;
}

export interface QuoteDashboardSummary {
  totalQuotes: number;
  drafted: number;
  skipped: number;
  noThreadFound: number;
  notProcessed: number;
}

export interface GetQuoteDashboardResponse {
  success: boolean;
  quotes?: OpenQuote[];
  summary?: QuoteDashboardSummary;
  error?: string;
}

export interface QuoteDetailHeader {
  quoteNum: number;
  custNum: number;
  custId: string;
  customerName: string;
  contactName: string;
  shipToNum: string;
  shipToName: string;
  shipToAddress: string;
  shipToContactName: string;
  btCustName: string;
  btCustId: string;
  btAddress: string;
  btContactName: string;
  dateQuoted: string;
  followUpDate: string;
  expirationDate: string;
  dueDate: string;
  docTotalQuote: number;
  quoteComment: string;
  entryPerson: string;
  currentStage: string;
  quoteClosed: boolean;
  expired: boolean;
  currencyCode: string;
  internalComment: string;
}

export interface QuoteDetailLine {
  quoteLine: number;
  partNum: string;
  partNumUM: string; // UM part number (UD_PartNumUM_c), resolved from partNum
  lineDesc: string;
  sellingExpectedQty: number;
  docExpUnitPrice: number; // Base (list) unit price before discount
  discountPercent: number; // Line discount percentage
  docOrderBasedPrice: number; // Net unit price after discount
  docTotalPrice: number;
  ium: string;
}

export interface QuoteDetail {
  header: QuoteDetailHeader;
  lines: QuoteDetailLine[];
}

export interface GetQuoteDetailResponse {
  success: boolean;
  quoteDetail?: QuoteDetail;
  error?: string;
}

// ---------------------------------------------------------------------------
// Customer Dashboard
// ---------------------------------------------------------------------------

export interface CustomerSearchResult {
  custNum: number;
  custId: string;
  name: string;
}

export interface ShipTo {
  shipToNum: string;
  name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  incotermCode: string;
  incotermsDescription: string;
  shipViaCode: string;
  shipViaDescription: string;
  contactName: string;
}

export interface CustomerHeader {
  custNum: number;
  custId: string;
  name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  termsCode: string;
  termsDescription: string;
  currencyCode: string;
  incotermCode: string;
  incotermsDescription: string;
  shipViaCode: string;
  shipViaDescription: string;
}

export interface CustomerDetail {
  header: CustomerHeader;
  shipTos: ShipTo[];
}

export interface CustomerQuoteHistoryItem {
  quoteNum: number;
  dateQuoted: string;
  docTotalQuote: number;
  currencyCode: string;
  shipToNum: string;
  shipToName: string;
  quoteClosed: boolean;
}

export interface CustomerOrderHistoryItem {
  orderNum: number;
  orderDate: string;
  docOrderAmt: number;
  currencyCode: string;
  shipToNum: string;
  shipToName: string;
  openOrder: boolean;
  voidOrder: boolean;
}

export interface SearchCustomersResponse {
  success: boolean;
  customers?: CustomerSearchResult[];
  error?: string;
}

export interface GetCustomerDetailResponse {
  success: boolean;
  customerDetail?: CustomerDetail;
  error?: string;
}

export interface GetCustomerQuoteHistoryResponse {
  success: boolean;
  quoteHistory?: CustomerQuoteHistoryItem[];
  error?: string;
}

export interface GetCustomerOrderHistoryResponse {
  success: boolean;
  orderHistory?: CustomerOrderHistoryItem[];
  error?: string;
}
